/**
 * textCase.ts
 * ---------------------------------------------------------------------------
 * Display-time text formatting for the Travel Advocates portal.
 *
 * These are pure, framework-agnostic string functions — no DOM/React
 * dependency, so they work the same in React web pages, table cells,
 * React Native screens, or plain TS files. They do NOT touch the database;
 * data is formatted only at the point of display.
 *
 *   toTitleCase()          -> for names: quotation name, place name,
 *                             excursion name, hotel name, etc.
 *   capitalizeFirstLetter()-> for descriptions: only the first letter is
 *                             capitalized, everything else is left as-is.
 * ---------------------------------------------------------------------------
 */

/**
 * "Minor" words that stay lowercase in Title Case, unless they are the
 * first or last word of the string (standard English title-casing rule).
 */
const MINOR_WORDS = new Set([
  'a', 'an', 'the',
  'and', 'but', 'or', 'nor',
  'as', 'at', 'by', 'for', 'in', 'of', 'on', 'per', 'to', 'via', 'with',
]);

/**
 * Fixed abbreviations/codes that must always render fully uppercase,
 * no matter where they appear in a string — e.g. "sta5700" -> "STA5700",
 * "STA-0163" stays "STA-0163", "sta005's quotation" -> "STA005's Quotation".
 *
 * Add more codes here later if new fixed abbreviations show up
 * (e.g. FORCED_ACRONYMS = ['STA', 'GST', 'PAN']).
 */
const FORCED_ACRONYMS = ['STA', 'SIC', 'BB', 'HB', 'AI'];

/**
 * Forces each entry in FORCED_ACRONYMS to uppercase wherever it appears as
 * a standalone token or immediately prefixed to digits.
 *
 * Deliberately does NOT touch normal English words that merely start with
 * the same letters — "Started", "Station", "Astana" are left alone, because
 * the match requires what follows "sta" to be a digit or non-letter
 * (a real boundary), not another letter.
 */
function forceAcronyms(text: string): string {
  let result = text;
  for (const acronym of FORCED_ACRONYMS) {
    const pattern = new RegExp(`(^|[^a-zA-Z0-9])${acronym}(?![a-zA-Z])`, 'gi');
    result = result.replace(pattern, (_m, boundary) => boundary + acronym.toUpperCase());
  }
  return result;
}

/**
 * Converts a string to Title Case for display.
 *
 *   toTitleCase("goa beach excursion")      -> "Goa Beach Excursion"
 *   toTitleCase("lord of the rings tour")   -> "Lord of the Rings Tour"
 *   toTitleCase("o'brien's travels")        -> "O'Brien's Travels"
 *   toTitleCase("sta5700 - skyline - mp")   -> "STA5700 - Skyline - Mp"
 *   toTitleCase("sta005's quotation")       -> "STA005's Quotation"
 *   toTitleCase("grand world (central hotel)") -> "Grand World (Central Hotel)"
 *
 * Rules applied:
 * - Every word gets its first letter capitalized, EXCEPT minor words
 *   (of, the, and, in, ...) in the middle of the string — those stay
 *   lowercase, unless they're the first or last word.
 * - Hyphenated words get each segment capitalized ("baden-baden" -> "Baden-Baden").
 * - Leading punctuation like "(", '"', "[", "{" doesn't block capitalization —
 *   "(central" -> "(Central", not "(central".
 * - Apostrophes are handled correctly: name-internal apostrophes are
 *   capitalized ("o'brien" -> "O'Brien"), but a trailing possessive "'s"
 *   is left lowercase ("goa's" -> "Goa's", not "Goa'S").
 * - Whitespace BETWEEN words is collapsed to a single space, but leading and
 *   trailing whitespace is PRESERVED. This function runs on every keystroke
 *   of controlled inputs (value={toTitleCase(...)}), so trimming here would
 *   eat the space the user just typed between two words ("Executive " ->
 *   "Executive"), making it impossible to type multi-word names. Trimming
 *   for persistence happens at save time, not here.
 * - null / undefined / empty / whitespace-only input safely returns ''.
 */
export function toTitleCase(input?: string | null): string {
  if (!input || !input.trim()) return '';

  const leadingWhitespace = input.match(/^\s*/)![0];
  const trailingWhitespace = input.match(/\s*$/)![0];

  const words = input.trim().toLowerCase().split(/\s+/);
  const lastIndex = words.length - 1;

  const titleCased = words
    .map((word, index) => {
      const isEdgeWord = index === 0 || index === lastIndex;
      if (!isEdgeWord && MINOR_WORDS.has(word)) {
        return word;
      }

      // Capitalize the first letter, letters right after a hyphen, and
      // letters right after other leading punctuation like "(", '"', "[", "{"
      // (e.g. "(central hotel)" -> "(Central Hotel)"). Apostrophe is
      // deliberately excluded here — it has its own dedicated handling
      // just below, with the trailing-possessive exception.
      let capitalized = word.replace(
        /(^|[-(["{])([a-z])/g,
        (_m, boundary, letter) => boundary + letter.toUpperCase()
      );

      // Capitalize letters right after an apostrophe (e.g. "o'brien" -> "O'Brien"),
      // but skip a trailing possessive "'s" (e.g. "goa's" stays "Goa's").
      capitalized = capitalized.replace(/'([a-z])/g, (m, letter, offset, str) => {
        const isTrailingPossessive = letter === 's' && offset + 2 === str.length;
        return isTrailingPossessive ? m : "'" + letter.toUpperCase();
      });

      return capitalized;
    })
    .join(' ');

  return leadingWhitespace + forceAcronyms(titleCased) + trailingWhitespace;
}

/**
 * Capitalizes only the very first letter of a string (e.g. a description),
 * leaving everything else exactly as the user typed it.
 *
 *   capitalizeFirstLetter("this trip includes 3 nights in GOA.")
 *     -> "This trip includes 3 nights in GOA."
 *   capitalizeFirstLetter("this trip covers sta5700 package fully.")
 *     -> "This trip covers STA5700 package fully."
 *
 * - Skips leading whitespace/punctuation/numbers to find the first letter,
 *   so " 'premium package' details" -> " 'Premium package' details".
 * - Does NOT touch casing anywhere else in the string.
 * - null / undefined / empty input safely returns ''/input unchanged.
 */
export function capitalizeFirstLetter(input?: string | null): string {
  if (!input) return '';

  const match = input.match(/[a-zA-Z]/);
  let result = input;
  if (match && match.index !== undefined) {
    const idx = match.index;
    result = input.slice(0, idx) + input.charAt(idx).toUpperCase() + input.slice(idx + 1);
  }

  // Force fixed codes (e.g. STA) to uppercase even inside descriptions,
  // without touching anything else in the string.
  return forceAcronyms(result);
}